import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Wallet, Search, FileText } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  banco_pos: "Crédito (HE)",
  banco_neg: "Débito",
  banco_pago: "Pago",
  banco_gozado: "Gozado",
  compensacao: "Compensação",
};

export default function BancoHorasPage() {
  const [data, setData] = useState({ items: [], totals: {} });
  const [search, setSearch] = useState("");
  const [openExtrato, setOpenExtrato] = useState(false);
  const [extrato, setExtrato] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/banco-horas/saldos");
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((i) =>
      [i.matricula, i.nome, i.cargo, i.setor].join(" ").toLowerCase().includes(q));
  }, [data.items, search]);

  const openColaborador = async (mat) => {
    try {
      const { data } = await api.get(`/banco-horas/extrato/${mat}`);
      setExtrato(data);
      setOpenExtrato(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="banco-horas-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Operacional · Banco de Horas</div>
        <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
          <Wallet className="h-7 w-7 text-[#234935]" /> Banco de Horas — Saldos
        </h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
          Saldo consolidado por colaborador, calculado automaticamente a partir dos lançamentos de horas extras (banco positivo, banco gozado, banco pago, compensação).
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Saldo total" value={`${(t.saldo_total || 0).toFixed(2)}h`} accent={t.saldo_total >= 0 ? "emerald" : "rose"} />
        <KpiTile label="Total créditos" value={`${(t.creditos_total || 0).toFixed(2)}h`} accent="emerald" />
        <KpiTile label="Total débitos" value={`${(t.debitos_total || 0).toFixed(2)}h`} accent="rose" />
        <KpiTile label="Colaboradores com saldo" value={t.colaboradores_com_saldo || 0} accent="ochre" />
      </div>

      <div className="rounded-lg border border-border bg-white p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Buscar matrícula, nome, setor..." className="pl-9" />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[110px]">Matrícula</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Cargo</TableHead>
            <TableHead>Setor</TableHead>
            <TableHead className="text-right">Créditos</TableHead>
            <TableHead className="text-right">Débitos</TableHead>
            <TableHead className="text-right">Saldo</TableHead>
            <TableHead className="text-right">Movimentos</TableHead>
            <TableHead className="w-[80px]"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {!filtered.length ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Nenhum saldo encontrado</TableCell></TableRow>
            ) : filtered.map((i) => (
              <TableRow key={i.matricula}>
                <TableCell className="font-mono text-[12px]">{i.matricula}</TableCell>
                <TableCell className="font-medium">{i.nome}</TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground">{i.cargo}</TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground">{i.setor}</TableCell>
                <TableCell className="text-right text-emerald-700 font-mono">+{i.creditos.toFixed(2)}</TableCell>
                <TableCell className="text-right text-rose-700 font-mono">-{i.debitos.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono font-bold">
                  <span className={i.saldo >= 0 ? "text-emerald-700" : "text-rose-700"}>
                    {i.saldo > 0 ? "+" : ""}{i.saldo.toFixed(2)}h
                  </span>
                </TableCell>
                <TableCell className="text-right text-[12px]">{i.movimentos}</TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => openColaborador(i.matricula)}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> Extrato
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={openExtrato} onOpenChange={setOpenExtrato}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              Extrato — {extrato?.colaborador?.nome} ({extrato?.colaborador?.matricula})
            </DialogTitle>
          </DialogHeader>
          <div className="bg-[#F1ECE0] border border-[#C5A059]/30 rounded-lg p-4 mb-3 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#5A4519]">Saldo atual</div>
              <div className={`text-3xl font-bold font-display ${(extrato?.saldo_atual || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {(extrato?.saldo_atual || 0) >= 0 ? "+" : ""}{(extrato?.saldo_atual || 0).toFixed(2)}h
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Movimentos</div>
              <div className="text-2xl font-display font-bold">{extrato?.total_movimentos || 0}</div>
            </div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Crédito</TableHead>
              <TableHead className="text-right">Débito</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!extrato?.extrato?.length ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Sem movimentações</TableCell></TableRow>
              ) : extrato.extrato.map((m, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-[12px]">{m.data}</TableCell>
                  <TableCell className="capitalize text-[12.5px]">{m.origem?.replace("_", " ")}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={m.horas > 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}>
                      {TYPE_LABEL[m.tipo_movimento] || m.tipo_movimento}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-emerald-700 font-mono">{m.credito ? `+${m.credito.toFixed(2)}` : "—"}</TableCell>
                  <TableCell className="text-right text-rose-700 font-mono">{m.debito ? `-${m.debito.toFixed(2)}` : "—"}</TableCell>
                  <TableCell className="text-right font-mono font-bold">
                    <span className={m.saldo_corrente >= 0 ? "text-emerald-700" : "text-rose-700"}>
                      {m.saldo_corrente.toFixed(2)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ label, value, accent }) {
  const tone = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    ochre: "bg-[#F1ECE0] border-[#C5A059]/30 text-[#5A4519]",
  }[accent] || "bg-white border-slate-200";
  return (
    <div className={`rounded-lg border p-4 ${tone}`}>
      <div className="text-[10.5px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-3xl font-bold font-display mt-1 leading-tight">{value}</div>
    </div>
  );
}
